// habitScheduleOverrides.js — local-only metadata for forward-only
// frequency changes on habits.
//
// Why this exists
// ---------------
// When the user edits a habit's frequency they can choose to apply the
// change "retroactively" or "only from today forward". Retroactive just
// overwrites habit.frequency (the normal mutation path). Forward-only
// needs us to remember the PREVIOUS schedule + the cutoff date so past
// days keep counting under the old rule and future days use the new one.
//
// We store this client-side in localStorage instead of adding a Supabase
// column so we don't block on a server migration. If the user signs in on
// another device the override won't follow — documented tradeoff. When
// we're ready to promote this to the server, swap the body of this file
// for a Supabase-backed implementation and all call sites keep working.
//
// Shape per habit:
//   {
//     prevFrequency:   'Daily' | 'Weekdays' | 'Custom',
//     prevCustomDays:  number[] | null,    // 0..6 (Sun..Sat)
//     frequencyChangedAt: 'YYYY-MM-DD',    // first day the NEW schedule applies
//   }
//
// Missing entry === no split; the habit's current fields are the whole truth.

const KEY = 'proxera.habitScheduleOverrides.v1';

const listeners = new Set();

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch { return {}; }
}
function write(next) {
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch {}
}

// Cached snapshot for useSyncExternalStore. Same pattern as audioPlayerStore:
// SES requires a referentially stable value between mutations or it bails
// into an infinite render loop.
let snapshot = read();

function emit() {
  snapshot = read();
  listeners.forEach(fn => { try { fn(snapshot); } catch {} });
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getSnapshot() { return snapshot; }

export function getOverride(habitId) {
  return snapshot[habitId] || null;
}

export function setOverride(habitId, { prevFrequency, prevCustomDays, frequencyChangedAt }) {
  const all = read();
  all[habitId] = {
    prevFrequency,
    prevCustomDays: Array.isArray(prevCustomDays) ? [...prevCustomDays] : null,
    frequencyChangedAt,
  };
  write(all);
  emit();
}

export function clearOverride(habitId) {
  const all = read();
  if (!(habitId in all)) return;
  delete all[habitId];
  write(all);
  emit();
}
